import { useState, useEffect, useRef } from 'react';

/**
 * useProgressiveText - Smoothly reveals text character-by-character.
 *
 * Updates React state on every RAF frame (~16ms). This is efficient because
 * StreamingContent only runs MarkdownRenderer at line boundaries — between
 * newlines, each state update just refreshes a plain text div (trivially cheap).
 *
 * IMPORTANT: state is initialized to targetText.length, NOT 0. This is critical
 * for the navigation case: when the user leaves a streaming conversation and
 * comes back, the component remounts with targetText already containing all the
 * text accumulated while they were away. Initializing to 0 would replay the
 * entire text character-by-character (~4s for 2000 chars at 8 chars/frame),
 * creating a visible "rollback" effect where markdown formatting builds up
 * line-by-line from the top. By starting at the current length, only genuinely
 * new deltas arriving after remount get the typewriter animation.
 *
 * @param targetText - The full accumulated text (grows with each delta)
 * @param isStreaming - Whether streaming is active
 * @param charsPerFrame - Chars to advance per frame (~16ms). Default 8.
 */
export function useProgressiveText(
  targetText: string,
  isStreaming: boolean,
  charsPerFrame = 8,
): string {
  // Initialize to targetText.length so that on remount (e.g. navigating back to
  // a streaming conversation), already-received content appears instantly and only
  // truly new deltas get the typewriter animation.
  const [displayedLength, setDisplayedLength] = useState(targetText.length);
  const rafRef = useRef(0);
  const cursorRef = useRef(targetText.length);
  const targetLenRef = useRef(0);

  targetLenRef.current = targetText.length;

  // When not streaming, show everything immediately
  useEffect(() => {
    if (!isStreaming) {
      cancelAnimationFrame(rafRef.current);
      cursorRef.current = targetText.length;
      setDisplayedLength(targetText.length);
    }
  }, [isStreaming, targetText.length]);

  // Animate reveal — one state update per RAF frame
  useEffect(() => {
    if (!isStreaming) return undefined;

    const animate = () => {
      if (cursorRef.current < targetLenRef.current) {
        cursorRef.current = Math.min(
          cursorRef.current + charsPerFrame,
          targetLenRef.current,
        );
        setDisplayedLength(cursorRef.current);
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    if (cursorRef.current < targetLenRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(animate);
    }

    return () => cancelAnimationFrame(rafRef.current);
  }, [targetText.length, isStreaming, charsPerFrame]);

  // Reset on clear — dep is targetText (not a derived boolean) so the effect
  // fires whenever targetText changes. The inner guard limits actual resets to
  // the empty→empty or non-empty→empty transition.
  useEffect(() => {
    if (targetText.length === 0) {
      cursorRef.current = 0;
      setDisplayedLength(0);
    }
  }, [targetText]);

  if (!isStreaming) return targetText;
  return targetText.slice(0, displayedLength);
}
